import React, { useContext, useState } from "react";
import { ShieldLockRegular, WarningRegular, ErrorCircleRegular } from "@fluentui/react-icons";
import { Spinner, SpinnerSize, Stack, DefaultButton } from "@fluentui/react";
import { AppStateContext } from "../../state/AppProvider";
import styles from "./B2CAuthGate.module.css";

interface B2CAuthGateProps {
    onSignOut: () => void;
    onIframeSignIn: () => void;
    children: React.ReactNode;
}

export const B2CAuthGate = ({ onSignOut, onIframeSignIn, children }: B2CAuthGateProps) => {
    const appStateContext = useContext(AppStateContext);
    const { b2cInternalCheckComplete, b2cInternalAuthState, frontendSettings } = appStateContext?.state || {};

    // Sign-out triggers an async MSAL redirect before the page reloads; show a loading
    // screen in the meantime so the gate doesn't appear frozen after the click.
    const [isSigningOut, setIsSigningOut] = useState(false);
    const handleSignOut = () => {
        setIsSigningOut(true);
        onSignOut();
    };

    if (isSigningOut) {
        return (
            <div className={styles.loadingScreen}>
                <Spinner size={SpinnerSize.large} label="Signing out..." ariaLive="assertive" />
            </div>
        );
    }

    if (b2cInternalAuthState === 'iframe_signin_required') {
        /* Cross-origin iframe: browsers block popups without a user gesture, so we render
           a sign-in button. The click provides the gesture that lets loginPopup open. */
        return (
            <Stack className={styles.gateContainer}>
                <ShieldLockRegular className={styles.gateIcon} style={{color: 'darkorange', height: "200px", width: "200px"}}/>
                <h2 className={styles.gateSubtitle}>Please sign in to continue.</h2>
                <Stack tokens={{ childrenGap: 12 }}>
                    <DefaultButton
                        text="Sign In"
                        onClick={onIframeSignIn}
                        className={styles.gateButton}
                    />
                </Stack>
            </Stack>
        );
    }

    if (!b2cInternalCheckComplete || b2cInternalAuthState === 'loading') {
        /* B2C Internal Auth: Loading - gate the chat UI until auth is resolved */
        return (
            <div className={styles.loadingScreen}>
                <Spinner size={SpinnerSize.large} label="Loading..." ariaLive="assertive" />
                <h2 style={{ fontSize: "18px", marginTop: "20px", textAlign: "center" }}>
                    Verifying your session...
                </h2>
            </div>
        );
    }

    if (b2cInternalAuthState === 'wrong_url') {
        /* B2C Internal Auth: Wrong URL Screen - user has a subscription but not for this URL's programs */
        return (
            <Stack className={styles.gateContainer}>
                <WarningRegular className={styles.gateIcon} style={{color: 'darkorange', height: "200px", width: "200px"}}/>
                <h1 className={styles.gateTitle}>Incorrect URL</h1>
                <h2 className={styles.gateSubtitle}>
                    It looks like your account is associated with a different program.
                </h2>
                <h2 className={styles.gateSubtitle}>
                    Please check the URL and try again, or contact your administrator for the correct link.
                </h2>
                <Stack tokens={{ childrenGap: 12 }}>
                    <DefaultButton
                        text="Sign in with a different account"
                        onClick={handleSignOut}
                        className={styles.gateButton}
                    />
                </Stack>
            </Stack>
        );
    }

    if (b2cInternalAuthState === 'pending') {
        /* B2C Internal Auth: Pending Access Screen */
        return (
            <Stack className={styles.gateContainer}>
                <ShieldLockRegular className={styles.gateIcon} style={{color: 'darkorange', height: "200px", width: "200px"}}/>
                <h1 className={styles.gateTitle}>Access Pending Approval</h1>
                <h2 className={styles.gateSubtitle}>
                    {frontendSettings?.matched_program_name ? (
                        <>You have been assigned to&nbsp;<b>{frontendSettings.matched_program_name}</b>.</>
                    ) : (
                        <>Your access request has been submitted.</>
                    )}
                </h2>
                <h2 className={styles.gateSubtitle}>
                    An administrator will review your access request shortly. You will receive an email notification once your access has been approved. Thank you for your patience.
                </h2>
                <h2 className={styles.gateSubtitle}>
                    If you need immediate assistance, please contact your administrator.
                </h2>
                <Stack tokens={{ childrenGap: 12 }}>
                    <DefaultButton
                        text="Sign in with a different account"
                        onClick={handleSignOut}
                        className={styles.gateButton}
                    />
                </Stack>
            </Stack>
        );
    }

    if (b2cInternalAuthState === 'no_access') {
        /* B2C Internal Auth: No Access Screen */
        return (
            <Stack className={styles.gateContainer}>
                <ErrorCircleRegular className={styles.gateIcon} style={{color: 'crimson', height: "200px", width: "200px"}}/>
                <h1 className={styles.gateTitle}>Unable to Verify Access</h1>
                <h2 className={styles.gateSubtitle}>
                    Something went wrong while verifying your access. This could be a temporary issue, or you may not be assigned to a program.
                </h2>
                <h2 className={styles.gateSubtitle}>
                    Please try again, or contact your administrator if the problem persists.
                </h2>
                <Stack tokens={{ childrenGap: 12 }}>
                    <DefaultButton
                        text="Try Again"
                        onClick={() => window.location.reload()}
                        className={styles.gateButton}
                    />
                </Stack>
            </Stack>
        );
    }

    return <>{children}</>;
};
